import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from '../../i18n';

export interface SkillInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  colorEnd: string;
  coverImage?: string;
}

interface SkillsViewProps {
  skills: SkillInfo[];
  onSkillSelect: (skillId: string) => void;
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  padding: 0 0 24px;
`;

const Title = styled.h2`
  font-family: 'Hanken Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-family: 'Hanken Sans', sans-serif;
  font-size: 13px;
  color: var(--color-text-muted);
  margin: 0 0 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Card = styled.button<{ $index: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  padding: 0;

  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: ${({ $index }) => $index * 80}ms;

  &:hover {
    border-color: var(--color-border);
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CoverHeader = styled.div`
  width: 100%;
  height: 100px;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const CardBody = styled.div`
  padding: 12px;
`;

const CardName = styled.div`
  font-family: 'Hanken Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 4px;
`;

const CardDescription = styled.div`
  font-family: 'Hanken Sans', sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-text-secondary);
`;

export const SkillsView: React.FC<SkillsViewProps> = ({ skills, onSkillSelect }) => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>{t('skills.title')}</Title>
      <Subtitle>{t('skills.subtitle')}</Subtitle>
      <Grid>
        {skills.map((skill, index) => (
          <Card key={skill.id} $index={index} onClick={() => onSkillSelect(skill.id)}>
            <CoverHeader>
              <img
                src={skill.coverImage || ''}
                alt={skill.name}
                loading={index < 4 ? 'eager' : 'lazy'}
              />
            </CoverHeader>
            <CardBody>
              <CardName>{skill.name}</CardName>
              <CardDescription>{skill.description}</CardDescription>
            </CardBody>
          </Card>
        ))}
      </Grid>
    </Container>
  );
};
